import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import moment from 'moment';
import { FormattingService } from '../services/formatting.service';
import { UntypedFormControl } from '@angular/forms';
import { LanguageService } from '../language/language.service';
import Pikaday from 'pikaday';
import { DEFAULT_DATE_FORMAT } from '../services/date.util';
import { autoCompleteDate } from '@app/shared/combined-date/combined-date.util';

@Component({
  selector: 'date-picker',
  template: `
		<div class="p-date input-group input-group-sm">
			<input class="form-control date-control" type="text" [(ngModel)]="model.dateValue" autocompletetype="" [inputFocus]="focus">
      <button class="btn btn-default" type="button" tabindex="-1"><i class="fa fa-calendar"></i></button>
		</div>
	`
})
export class DatePickerComponent implements OnInit, OnDestroy {
  @Input() public control:UntypedFormControl;
  @Output() public pickedDate:EventEmitter<Date> = new EventEmitter<Date>();
  @Input() public minDate:Date;
  @Input() public maxDate:Date;
  @Input() public focus:boolean;

  public model = {
    dateValue: null
  };

  private jInput:JQuery<HTMLElement>;
  private picker:any;

  constructor(
    private formattingService:FormattingService,
    private languageService:LanguageService,
    private elementRef:ElementRef<HTMLElement>
  ) {
    /**/
  }

  public ngOnInit():void {
    this.jInput = $(this.elementRef.nativeElement).find('input');

    const setPickedDate = (date:Date):void => {
      this.pickedDate.emit(date);
    };

    const pikadayOptions:Pikaday.PikadayOptions = {
      field: this.jInput[ 0 ],
      format: DEFAULT_DATE_FORMAT,
      onSelect: function() { // cannot be a fat-arrow function because of this
        const m = this.getMoment();
        if (m) {
          //make this a UTC date
          setPickedDate(moment.utc([
            m.year(),
            m.month(),
            m.date()
          ]).toDate());
        } else {
          setPickedDate(undefined);
        }
      },
      trigger: $(this.elementRef.nativeElement).find('.btn')[ 0 ],
      i18n: this.languageService.translateDatepicker(),
      minDate: this.minDate,
      maxDate: this.maxDate
    };

    this.picker = new Pikaday(pikadayOptions);

    this.jInput.inputmask(DEFAULT_DATE_FORMAT.toLowerCase(), { onKeyDown: this.onKeyDown });

    this.jInput.on('blur', () => this.updateDate(true));

    // init value
    this.initValue();

    // register listener
    this.listen();
  }

  public ngOnDestroy():void {
    if (this.jInput) {
      this.jInput.off('blur');
    }

    if (this.picker) {
      this.picker.destroy();
    }
  }

  private initValue():void {
    if (this.control?.value) {
      this.formatDate(this.control.value);
    }
  }

  private formatDate(date:Date | number):void {
    let formattedDate = null;
    if (date) {
      formattedDate = this.formattingService.formatDate(date);
    }

    if (this.picker) {
      this.picker.setDate(date, true);
    }

    this.model.dateValue = formattedDate;
  }

  private listen():void {
    this.pickedDate.subscribe(date => {
      this.formatDate(date);
    });

    this.control.valueChanges.subscribe(value => {
      this.formatDate(value);
    });
  }

  private onKeyDown = (e:KeyboardEvent):void => {
    const charCode = !e.charCode? e.which : e.charCode;

    setTimeout(() => {
      // triggers on Backspace or Delete
      if ((charCode === 8 || charCode === 49) && this.jInput.val() === '') {
        if (this.pickedDate) {
          this.pickedDate.emit(undefined);
        }
      }

      // triggers on TAB
      else if (charCode === 9) {

        if (this.jInput.val() !== '') {

          e.preventDefault();

          this.jInput.val(autoCompleteDate(this.jInput));
        }
      }

      // when the date is typed in manually and is complete
      else if(this.jInput.inputmask('isComplete')) {
        this.updateDate();
      }
    });
  };

  private updateDate = (autoComplete?:boolean) => {
    const dateValue = this.jInput.val();
    let parsedDate = null;
    if (dateValue) {
      parsedDate = this.formattingService.parseDate(autoComplete ? autoCompleteDate(this.jInput) : this.jInput.val());
    }
    if (this.picker) {
      this.picker.setDate(parsedDate, true);
    }

    this.pickedDate.emit(parsedDate);
  };
}